import { useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { toast } from "react-toastify";
import { apiFetch, sendValidateEmail } from "@common/lib/api";
import { fetchDoctors as fetchDoctorsAPI, createDoctor, updateDoctor, deleteDoctor } from "@common/lib/api/doctors";
import { Doctor, DoctorDocumentTypeContext, ValidateType } from "@common/utils/types";
import { HAPPY_SOAP } from "@common/utils/constants";
import { billingPlanQueryKey } from "./useBilling";
import { useAccountContext } from "@/state/providers/AccountProvider";

// Stable empty-array reference: returned whenever the query has no usable data (pending,
// disabled, errored, or malformed payload). A fresh [] literal here changes identity on every
// render, which churns useEffect deps in consumers like Calendar.tsx and triggers an infinite
// render loop ("Maximum update depth exceeded"). See EN-857.
const EMPTY_DOCTORS: Doctor[] = [];
const getDoctorsFromQueryData = (queryData: { data?: unknown } | null | undefined): Doctor[] => {
    return Array.isArray(queryData?.data) ? queryData.data : EMPTY_DOCTORS;
};

const getTotalFromQueryData = (queryData: { total?: unknown } | null | undefined): number => {
    return typeof queryData?.total === "number" ? queryData.total : 0;
};

export type DoctorPaginationModel = {
    page: number; // 1-based
    pageSize: number;
};

/**
 * React Query hook for managing doctors with server-side pagination.
 * Router-agnostic: accepts page/pageSize/search as plain params so it can be
 * used in tests and non-Router contexts. URL-sync lives in DoctorTable.
 */
export const useDoctors = (params?: { page?: number; pageSize?: number; search?: string }) => {
    const queryClient = useQueryClient();
    const { accountData } = useAccountContext();
    const clinicAccountId =
        accountData && typeof accountData === "object" && "id" in accountData && accountData.id
            ? String(accountData.id)
            : undefined;

    const page = Math.max(1, params?.page ?? 1);
    const pageSize = Math.max(1, params?.pageSize ?? 50);
    const search = params?.search ?? "";

    const queryKey = ["doctors", clinicAccountId, page, pageSize, search] as const;

    // Query for fetching doctors (scoped to resolved clinic account, with pagination and search)
    const query = useQuery({
        queryKey,
        queryFn: () =>
            fetchDoctorsAPI({
                page,
                limit: pageSize,
                search: search || undefined,
            }),
        enabled: Boolean(clinicAccountId),
        staleTime: 2 * 60 * 1000, // 2 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes
        placeholderData: (prev) => prev,
    });

    // Mutation for creating a doctor
    const createMutation = useMutation({
        mutationFn: async (doctor: Partial<Doctor>) => {
            if (!clinicAccountId) {
                throw new Error("No clinic account loaded");
            }
            const newDoctor = await createDoctor(doctor);

            // Add the default document for the doctor (Happy SOAP)
            if (newDoctor.id) {
                try {
                    await apiFetch(`doctors/${newDoctor.id}/documents`, {
                        method: "POST",
                        json: {
                            document_type_id: HAPPY_SOAP,
                            context: DoctorDocumentTypeContext.Default,
                        },
                    });
                } catch (error) {
                    // Ignore errors if document already exists
                    console.warn("Failed to add default document for doctor:", error);
                }

                // Send validation email if email is provided
                if (doctor.email) {
                    await sendValidateEmail(newDoctor.id, doctor.email, ValidateType.Doctor);
                }
            }

            return newDoctor;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["doctors", clinicAccountId] });
            queryClient.invalidateQueries({ queryKey: billingPlanQueryKey });
        },
    });

    // Mutation for updating a doctor
    const updateMutation = useMutation({
        mutationFn: ({ id, doctor }: { id: string; doctor: Partial<Doctor> }) => updateDoctor(id, doctor),
        onSuccess: () => {
            // On success, refresh all doctor cache entries so server truth is reflected.
            // On failure, callers that use optimistic updates perform their own rollback via
            // setQueryData(previousData) — no additional invalidation needed on the error path.
            queryClient.invalidateQueries({ queryKey: ["doctors", clinicAccountId] });
        },
    });

    // Mutation for deleting a doctor
    const deleteMutation = useMutation({
        mutationFn: (id: string) => deleteDoctor(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["doctors", clinicAccountId] });
            queryClient.invalidateQueries({ queryKey: billingPlanQueryKey });
        },
    });

    // Search all cached pages for this account — not just the currently displayed page.
    const getDoctorById = (id: string | null | undefined): Doctor | null => {
        if (!id) return null;
        const allPages = queryClient.getQueriesData<{ data: Doctor[]; total: number }>({
            queryKey: ["doctors", clinicAccountId],
        });
        for (const [, pageData] of allPages) {
            const found = getDoctorsFromQueryData(pageData).find((d) => d.id === id);
            if (found) return found;
        }
        return null;
    };

    return {
        query,
        queryKey,
        doctors: getDoctorsFromQueryData(query.data),
        total: getTotalFromQueryData(query.data),
        createMutation,
        updateMutation,
        deleteMutation,
        getDoctorById,
        clinicAccountId,
    };
};

/**
 * Lightweight hook for non-paginated access (dropdowns, lookups).
 * Fetches up to 1000 doctors with a stable query key so all callers share one cache entry.
 * Exposes isAtLimit when the clinic exceeds 1000 doctors — callers should surface a warning.
 */
export const useAllDoctors = () => {
    const { accountData } = useAccountContext();
    const clinicAccountId =
        accountData && typeof accountData === "object" && "id" in accountData && accountData.id
            ? String(accountData.id)
            : undefined;

    const query = useQuery({
        queryKey: ["doctors", clinicAccountId, 1, 1000, ""],
        queryFn: () => fetchDoctorsAPI({ page: 1, limit: 1000 }),
        enabled: Boolean(clinicAccountId),
        staleTime: 2 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
        // No placeholderData here: unlike the paginated useDoctors, useAllDoctors has a single
        // stable key, and keeping prior data would briefly show the PREVIOUS account's doctors
        // after an account switch before the new fetch completes. A failed refetch of the same
        // key still keeps the last good roster in data alongside isError (EN-1419).
    });

    const getDoctorById = (id: string | null | undefined): Doctor | null => {
        if (!id) return null;
        return getDoctorsFromQueryData(query.data).find((d) => d.id === id) ?? null;
    };

    const doctors = getDoctorsFromQueryData(query.data);
    const total = getTotalFromQueryData(query.data);
    const isAtLimit = total > doctors.length && doctors.length >= 1000;

    // Ref guard ensures Sentry/console fire at most once per mount even if isAtLimit
    // temporarily toggles through a loading cycle during background refetches.
    const atLimitReportedRef = useRef(false);
    useEffect(() => {
        if (isAtLimit && !atLimitReportedRef.current) {
            atLimitReportedRef.current = true;
            console.error(
                "[useAllDoctors] clinic has >1000 doctors — doctor list is truncated; dropdowns and initialization may be incomplete",
            );
            Sentry.captureMessage("useAllDoctors: clinic has >1000 doctors — list is truncated", "error");
            toast.warning("Doctor list exceeds 1,000 — some providers may not appear in dropdowns. Contact support.", {
                toastId: "doctors-at-limit",
                autoClose: false,
            });
        }
    }, [isAtLimit]);

    return {
        query,
        doctors,
        total,
        isAtLimit,
        isError: query.isError,
        getDoctorById,
    };
};
